/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import GameSetup from '../../Game/GameSetup';
import LocalizationComponent from '../../Localization/LocalizationComponent';
import StateDependencyComponent from '../../StateManager/StateDependencyComponent';
import StateEvent from '../../StateManager/StateEvent';
import StateReference from '../../StateManager/StateReference';
import InjectableComponent from './ComponentReference/InjectableComponent';
import { ReferenceGroup } from './ComponentReference/ReferenceGroup';
import ObservableStringComponent from './Observable/ObservableStringComponent';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Tracks simluated server time based on a seed value from the config. */
@cc._decorator.ccclass
export class ServerTimeManager extends StateDependencyComponent
{
    /** On which state should the server time simulation begin. */
    @property({ type: StateReference, visible: true })
    private _initOnState: StateReference = new StateReference();

    /** @hidden */
    private _gameSetupRef: InjectableComponent<GameSetup> = new InjectableComponent();
    /** @hidden */
    private _localRef: InjectableComponent<LocalizationComponent> = new InjectableComponent();

    /** @hidden */
    private _serverTimeObservableRef: InjectableComponent<ObservableStringComponent> =
        new InjectableComponent<ObservableStringComponent>(ReferenceGroup.MainGame, "ServerTime.String");
    /** Accessor for the observable server time string. */
    public get ServerTimeObservable(): ObservableStringComponent
    {
        return this._serverTimeObservableRef.Value;
    }

    /** Options to use when formatting the hours, minutes, and seconds values. */
    private _formatOptions: Intl.DateTimeFormatOptions =
    {
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short"
    };

    /** Local time at start of session. */
    private _localStartTime: number = 0;
    /** @hidden */
    private _serverStartTime: number = 0;

    /** @hidden */
    protected setContexts(): void
    {
        this._gameSetupRef.SetContext(GameSetup);
        this._serverTimeObservableRef.SetContext(ObservableStringComponent);
        this._localRef.SetContext(LocalizationComponent);
    }

    /** @hidden */
    protected start(): void
    {
        super.start([this._initOnState]);
    }

    /** @hidden */
    protected update(dt: number): void
    {
        if (!this._serverStartTime) { return; }

        const timeZoneOffset: number = this._gameSetupRef.Value.UseLocalTimeZone
            ? 0
            : this._gameSetupRef.Value.ServerTimeZoneOffset;
        let serverTime: number = (this._serverStartTime + timeZoneOffset) * 1000;
        serverTime += new Date().getTime() - this._localStartTime;
        let serverDate: Date = new Date(serverTime);

        let formattedString: string = serverDate.toLocaleTimeString(this._localRef.Value.CultureKey,
            this._formatOptions);
        this.ServerTimeObservable.Value = formattedString.split('UTC')[0] || formattedString;
    }

    /** @hidden */
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        if (event.State === this._initOnState.Value)
        {
            this._serverStartTime = this._gameSetupRef.Value.ServerTime;
            if (this._gameSetupRef.Value.UseLocalTimeZone === false)
            {
                this._formatOptions.timeZone = "UTC";
            }
            this._formatOptions.hour12 = this._gameSetupRef.Value.Use12HourClock;
            this._localStartTime = new Date().getTime();

            this.completeState(this._initOnState.Value);
        }
    }
}
